import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Plus,
  TriangleAlert,
} from "lucide-react-native";
import { memo } from "react";
import type { ReactNode } from "react";
import { Platform } from "react-native";

import type {
  PipelineTableRow,
  PipelineTableSortDirection,
  PipelineTableSortKey,
  PipelineTableTag,
} from "@/features/player-kanban/utils/pipelineTableRows";

import { TONE_CLASS } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import {
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/atoms/Table";
import { Text } from "@/components/atoms/Text";
import RemovablePill from "@/components/molecules/RemovablePill";
import { renderGprBadge } from "@/features/scouting/components/molecules/DataCell/DataCellView";
import { formatRelativeAgo } from "@/features/player-kanban/utils/cardUpdatedAgo";
import { getContractStatus } from "@/features/player-kanban/utils/playerFilter";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/**
 * Capability gate for the scroll host, NOT a layout fork (both platforms
 * render the identical table): web scrolls via one CSS overflow container,
 * native via ScrollView. Same line scouting's AllTargetsTableView forks on.
 */
const IS_WEB = Platform.OS === "web";

/** Placeholder for a cell whose value the backend has not supplied. */
const EMPTY_CELL = "—";

/**
 * Chips a Tags cell shows before the rest collapse into `+N`.
 * @remarks
 * Two, the same cut the Kanban card makes (`PlayerCardTagsContainer`), so the
 * same player reads the same way in both views.
 */
const MAX_VISIBLE_CHIPS = 2;

/**
 * Neutral chip chrome for the POS badge.
 * @remarks
 * The design fills this one at #e7e8e7 (231,232,231), which sits BETWEEN two
 * rungs and lands on neither: `surface-subtle` (242,241,241) is 17.3 away in
 * RGB and `surface-muted` (220,219,219) is 20.8, so subtle is the nearer of
 * the only two candidates and keeps the call. The honest caveat is that
 * against a white row the spec separates by 24 levels where subtle separates by
 * 13, so this chip reads lighter than the mock; muted would overshoot to 35.
 * **The missing rung is flagged rather than faked** — a raw hex here would
 * break the closed-token rule and the ratchet.
 *
 * A 2026-08-07 audit re-opened this because Raniel reported the chip still
 * wrong. It was NOT the fill: the rendered cell was `w-20 flex-none px-6`,
 * i.e. 32px of content width, and a three-letter position wrapped inside the
 * chip. See the column definition. Radius 2 takes `sm` (4): the alternative
 * rung is `none`, and a square-cornered chip is a different object.
 *
 * NOTE — deliberately different from the CARD's position chip, which is
 * `bg-surface-raised` + `text-content-primary` (`PlayerCardIdentityView`).
 * The two specs genuinely differ (card #f6f6f6, table #e7e8e7); flagged for
 * the designer rather than silently unified by an implementer.
 */
const POS_CHIP = "rounded-sm bg-surface-subtle px-2 py-1";

/**
 * Chip chrome for a List (watchlist) membership.
 * @remarks
 * The List cell spec fills at #f2f1f1, which is `surface-subtle` (242,241,241)
 * EXACTLY — no rounding, unlike POS and GPR. Same 8/4 inset and `sm` radius as
 * the other two chips, so the three read as one family across a row.
 */
const LIST_CHIP = "rounded-sm bg-surface-subtle px-2 py-1";

/**
 * The Verdict badge's two colours — the success pairing the chip already wore.
 * @remarks
 * `TONE_CLASS["success-soft"]` is `bg-success-0` + `text-success-900`, the
 * pairing the SE-5522 pixel audit ratified for this chip. Only the CHROME moves
 * to GPR's (below); the colours stay, which is exactly Raniel's ruling
 * (2026-08-07): "deixa na mesma lógica do GPR só que só com as duas devidas
 * cores" — GPR's shape, the verdict's own two colours.
 */
const VERDICT_TONE = TONE_CLASS["success-soft"];

/**
 * Verdict badge chrome, character-for-character `renderGprBadge`'s minus its
 * colours — see the note on {@link renderVerdictCell} for why it is copied
 * rather than shared.
 */
const VERDICT_CHIP =
  "web:inline-flex min-w-10 items-center self-start rounded-none px-2 py-1";

/** Column identities. Every column sorts; not every column is a field. */
type PipelineTableColumnKey =
  | "name"
  | "position"
  | "team"
  | "gpr"
  | "age"
  | "tags"
  | "contractMonths"
  | "activity"
  | "watchlists"
  | "pipeline"
  | "reports"
  | "verdict"
  | "actions";

/** One column of the pipeline table. */
interface PipelineTableColumn {
  /** Stable key — also the header's test identity. */
  readonly key: PipelineTableColumnKey;
  /** Header label, verbatim from the design. Empty for the trailing slot. */
  readonly label: string;
  /** Sort key the header toggles, or null when the column does not sort. */
  readonly sortKey: PipelineTableSortKey | null;
  /** Width and inline padding, shared by the header and body cells. */
  readonly cellClassName: string;
  /** Whether the cell centres its content (the design's numeric columns). */
  readonly isCentered?: boolean;
}

/**
 * Column set and order per Raniel's authoritative column list (SE-6554,
 * 2026-08-07): Player · POS · Club · GPR · Age · Tags · Months left ·
 * Activity · List · Pipeline · Reports · Verdict · [trailing].
 * @remarks
 * Widths come off the design snapped to the closed sizing ladder, ties
 * rounding down: POS 88 → `w-20` (80, midway between 80 and 96) and Club's 136
 * minimum → `min-w-32` (128, same tie). GPR/Age (64), Months left (96) and the
 * trailing slot (64) land on rungs exactly.
 *
 * **The design's 2:1 flex weighting is not reproducible.** The Table atom's own
 * base gives every cell `flex-1`, so the proportional columns already share the
 * slack evenly; doubling four of them would take an arbitrary `grow-[2]`, which
 * the closed ladder does not carry. The fixed-width columns instead take
 * `flex-none`, without which the atom's `flex-1` would stretch them off their
 * measured widths.
 *
 * ## Column-set history — read before changing this list
 *
 * This set has now flipped twice, so neither direction is a safe default:
 *
 * - 2026-08-06 — Stage KEPT over the artboard's objection, on the argument that
 *   the table is the flat view of a STAGED board.
 * - 2026-08-07 (morning) — List and Pipeline REMOVED against an intermediate
 *   prototype that dropped them.
 * - 2026-08-07 (this) — **REVERSED on both counts.** Raniel sent an
 *   authoritative prototype with "tem que ser essas colunas" and the list
 *   above: List and Pipeline are BACK, and **Stage is OUT**, superseding his
 *   own 2026-08-06 keep-ruling. The `stage` row-model field and comparator stay
 *   dormant in `pipelineTableRows`, exactly as List's did, so restoring the
 *   column is a definition rather than a re-derivation. Stage identity is not
 *   lost to machines either — the row's `data-testid` still encodes
 *   `<stageId>.<playerId>`, which is what the dual-view e2e assertion reads.
 *
 * Do not add or remove a column here from an artboard alone. Ask Raniel.
 */
const COLUMNS: readonly PipelineTableColumn[] = [
  {
    key: "name",
    label: "Player",
    sortKey: "name",
    cellClassName: "min-w-needs-semantic-240px px-6",
  },
  /* POS rounds UP (88 → `w-24`/96), against the round-down tie convention the
     other columns follow. Rounding down gave `w-20` (80), and with the spec's
     own 24px cell inset that leaves 80 − 48 = **32px** of content width. A POS
     chip spends 16 of that on its own padding, so a three-letter position
     ("CAM", "RWB") had ~16px for ~26px of glyphs and WRAPPED inside its chip —
     which is what made the badge read as a tall grey slab. 96 leaves 48, and
     the chip fits on one line. Measured, not guessed: the rendered cell chrome
     is `py-3 w-20 flex-none px-6`. */
  {
    key: "position",
    label: "POS",
    sortKey: "position",
    cellClassName: "w-24 flex-none px-6",
  },
  {
    key: "team",
    label: "Club",
    sortKey: "team",
    cellClassName: "min-w-32 px-6",
  },
  {
    key: "gpr",
    label: "GPR",
    sortKey: "gpr",
    cellClassName: "w-16 flex-none px-1",
    isCentered: true,
  },
  {
    key: "age",
    label: "Age",
    sortKey: "age",
    cellClassName: "w-16 flex-none px-1",
    isCentered: true,
  },
  {
    key: "tags",
    label: "Tags",
    sortKey: "tags",
    cellClassName: "min-w-40 px-2",
  },
  /* `Months left` in full. A pass shortened this to `Mo left` to stop the
     header wrapping in a 96px column; the authoritative prototype spells it
     out, wrap and all, so the wrap is accepted rather than designed around. */
  {
    key: "contractMonths",
    label: "Months left",
    sortKey: "contractMonths",
    cellClassName: "w-24 flex-none px-2",
    isCentered: true,
  },
  {
    key: "activity",
    label: "Activity",
    sortKey: "activity",
    cellClassName: "min-w-24 px-2",
  },
  {
    key: "watchlists",
    label: "List",
    sortKey: "watchlists",
    cellClassName: "min-w-40 px-2",
  },
  {
    key: "pipeline",
    label: "Pipeline",
    /* SE-6870: the cell now shows the card's stage name, so the header sorts
       by the long-dormant `stage` comparator (stage name, then rank) instead
       of the no-op `pipeline` one. */
    sortKey: "stage",
    cellClassName: "min-w-32 px-2",
  },
  {
    key: "reports",
    label: "Reports",
    sortKey: "reports",
    cellClassName: "px-3",
    isCentered: true,
  },
  {
    key: "verdict",
    label: "Verdict",
    sortKey: "verdict",
    cellClassName: "min-w-24 px-3",
  },
  /* The design's trailing 64px slot, RESERVED FOR WIDTH AND LEFT EMPTY.
     The final prototype draws two controls in it — a `+` in the header
     (add-column) and a `⋯` kebab on the hovered row (row menu) — and it draws
     them a second time after the earlier artboard did, so this is not an
     oversight on the design's part. Neither exists in the product: there is no
     add-column feature and no per-row menu, and there is no ticket defining
     what either would do. Both are FLAGGED for product rather than shipped as
     buttons that respond to a click by doing nothing. Wire them the moment
     there is something real behind them. */
  { key: "actions", label: "", sortKey: null, cellClassName: "w-16 flex-none" },
];

/** Props for PipelineTableView */
interface PipelineTableViewProps {
  /** Rows to render, already flattened and sorted by the container. */
  readonly rows: PipelineTableRow[];
  /** Active sort column, or null for the natural stage-then-rank order. */
  readonly sortKey: PipelineTableSortKey | null;
  /** Active sort direction. */
  readonly sortDirection: PipelineTableSortDirection;
  /** Builds the press handler for a sortable header. */
  readonly createSortHandler: (key: PipelineTableSortKey) => () => void;
  /** Builds the press handler for a row. */
  readonly createRowPressHandler: (playerId: string) => () => void;
  /**
   * Builds a tag's remove handler. Omitted for viewers who may not edit the
   * board, which is what drops the design's `×` from every chip.
   */
  readonly createTagRemoveHandler?: (
    playerId: string,
    tagId: string
  ) => () => void;
  /**
   * Builds a row's add-tag handler. Omitted for viewers who may not edit,
   * which is what drops the design's `Add` affordance.
   */
  readonly createAddTagHandler?: (playerId: string) => () => void;
}

/** What a cell renderer needs beyond its row. */
interface CellContext {
  /** Builds the press handler for a row. */
  readonly createRowPressHandler: (playerId: string) => () => void;
  /** Builds a tag's remove handler, when the viewer may edit. */
  readonly createTagRemoveHandler?: (
    playerId: string,
    tagId: string
  ) => () => void;
  /** Builds a row's add-tag handler, when the viewer may edit. */
  readonly createAddTagHandler?: (playerId: string) => () => void;
}

/**
 * The muted em-dash a cell shows when the board has no value for it.
 * @returns The placeholder element
 */
const renderEmpty = () => (
  <Text
    variant="unstyled"
    className="font-body text-title-sm text-content-muted"
  >
    {EMPTY_CELL}
  </Text>
);

/**
 * Plain 16px value text — the design's default cell.
 * @param value - Text to render
 * @returns The cell content
 */
const renderValue = (value: string) => (
  <Text
    variant="unstyled"
    numberOfLines={1}
    className="font-body text-title-sm text-content-secondary"
  >
    {value}
  </Text>
);

/**
 * The `+N` counter that stands for chips past the visible cut.
 * @param overflow - How many chips are collapsed
 * @returns The counter element, or null when nothing overflowed
 */
const renderOverflow = (overflow: number) =>
  overflow > 0 ? (
    <Text variant="micro" className="text-content-secondary">
      {`+${overflow}`}
    </Text>
  ) : null;

/**
 * Player name — the row's press target, opening the preview drawer.
 * @param row - Row being rendered
 * @param ctx - Shared cell context
 * @returns The cell content
 */
const renderNameCell = (row: PipelineTableRow, ctx: CellContext) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={`Open ${row.playerName}`}
    onPress={ctx.createRowPressHandler(row.playerId)}
  >
    <Text
      variant="unstyled"
      numberOfLines={1}
      className="font-body text-title-sm text-content-primary"
    >
      {row.playerName}
    </Text>
  </Pressable>
);

/**
 * Club, name only.
 * @remarks
 * SE-6872: the leading club glyph is REMOVED. The design drew a country-flag
 * emoji in front of the club name (SE-6554), but with no real club icon
 * assets it rendered as a broken/placeholder graphic on platforms without
 * flag-emoji support and read as noise on every row (Sprint 18 staging QA).
 * The club NAME stays; reintroducing club iconography once real assets exist
 * is a new ticket. `row.clubCountry` stays on the row model, dormant, exactly
 * as `stage` did while its column was out — restoring the glyph is a
 * definition rather than a re-derivation.
 * @param row - Row being rendered
 * @returns The cell content
 */
const renderClubCell = (row: PipelineTableRow) => {
  if (!row.teamName) return renderEmpty();

  return renderValue(row.teamName);
};

/**
 * Months left on the contract.
 * @remarks
 * The design marks some values with a warning triangle and leaves others plain
 * at the same number (⚠8 on one row, a bare 8 two rows down), so the mock
 * carries no threshold to copy. Rather than invent one, this reuses the rule
 * the board already ships: `getContractStatus`, whose `Expiring Soon` bucket
 * (1–6 months) and `Free Agent` bucket (0) are what the contract-status filter
 * has always meant by "running out".
 *
 * The warning ink is `warning-600` (215,108,31), which the design names
 * exactly; `status-warning` — the semantic name, and what this rendered before
 * — resolves to `warning-500` (231,120,40), one rung lighter. `warning-600` is
 * the raw palette tier, but it is the only rung that hits the design and it
 * flips properly in dark (253,173,116), which is why the contracts and
 * squad-planning surfaces already spell it the same way.
 * @param row - Row being rendered
 * @returns The cell content
 */
const renderContractMonthsCell = (row: PipelineTableRow) => {
  if (row.contractMonths == null) return renderEmpty();

  const status = getContractStatus(row.contractMonths);
  const isRunningOut = status === "Expiring Soon" || status === "Free Agent";

  if (!isRunningOut) return renderValue(String(row.contractMonths));

  return (
    <Stack direction="horizontal" className="items-center gap-1">
      <Icon
        as={TriangleAlert}
        size="sm"
        className="text-status-warning-strong"
      />
      <Text
        variant="unstyled"
        className="font-body text-title-sm text-status-warning-strong"
      >
        {String(row.contractMonths)}
      </Text>
    </Stack>
  );
};

/** Props for one removable tag chip. */
interface TagChipProps {
  /** The row the chip belongs to. */
  readonly row: PipelineTableRow;
  /** The tag this chip represents. */
  readonly tag: PipelineTableTag;
  /** Builds the remove handler, when the viewer may edit. */
  readonly createTagRemoveHandler?: (
    playerId: string,
    tagId: string
  ) => () => void;
}

/**
 * One tag chip — removable when the viewer may edit the board.
 * @remarks
 * A component rather than an inline arrow in the parent's `map`, so the
 * bound remove handler is a stable reference per chip
 * (`react-perf/jsx-no-new-function-as-prop`).
 *
 * The tone is `info-subtle` rather than the card's `info-soft`: this design
 * fills the chip at rgb(235,248,254), which is the `info/info0` variable the
 * subtle pairing binds, not the darker `info/info50` the card was audited to.
 * @param root0 - Chip props
 * @param root0.row - The row the chip belongs to
 * @param root0.tag - The tag this chip represents
 * @param root0.createTagRemoveHandler - Builds the remove handler
 * @returns The chip element
 */
const TagChip = ({ row, tag, createTagRemoveHandler }: TagChipProps) => {
  if (!createTagRemoveHandler) {
    return (
      <Box className={`self-start rounded-sm bg-status-info-surface px-2 py-1`}>
        <Text
          variant="caption"
          numberOfLines={1}
          className="max-w-40 text-status-info-strong"
        >
          {tag.name}
        </Text>
      </Box>
    );
  }

  return (
    <RemovablePill
      label={tag.name}
      tone="info-subtle"
      onRemove={createTagRemoveHandler(row.playerId, tag.id)}
      removeAccessibilityLabel={`Remove ${tag.name} from ${row.playerName}`}
    />
  );
};

/**
 * The Tags cell's `Add` affordance — opens the board's tag selector.
 * @remarks
 * It is a BARE TEXT affordance, not a chip: the prototype draws a plain grey
 * `+ Add` in the empty cell, where dev had a bordered white pill that read as
 * a filled value and competed with the real tag chips above and below it. The
 * `py-1` survives the border's removal so the press target keeps the same
 * height as a chip row; the horizontal padding does not, so the glyph lines up
 * on the cell's own left edge with every other column.
 *
 * It stays at `micro`/`content-muted`. A pass bumped it a rung on the theory
 * that Raniel's "não consigo adicionar uma tag" meant the affordance was too
 * faint to read as a control — he later resolved that himself ("eu entendi
 * agora a lógica do add, só permite se for o meu pipeline"), so the faintness
 * theory is dead and the prototype's own weight stands.
 * @param row - Row being rendered
 * @param createAddTagHandler - Builds the row's add-tag handler
 * @param showLabel - Whether to draw the `Add` word beside the `+`
 * @returns The affordance element
 */
const renderAddTag = (
  row: PipelineTableRow,
  createAddTagHandler: (playerId: string) => () => void,
  showLabel: boolean
) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={`Add a tag to ${row.playerName}`}
    onPress={createAddTagHandler(row.playerId)}
  >
    <Stack
      direction="horizontal"
      className="items-center gap-1 self-start py-1"
    >
      <Icon as={Plus} size="2xs" className="text-content-muted" />
      {showLabel ? (
        <Text variant="micro" className="text-content-muted">
          Add
        </Text>
      ) : null}
    </Stack>
  </Pressable>
);

/**
 * Tag chips, collapsing the rest into `+N`, with the design's `Add` affordance.
 * @remarks
 * `Add` opens the board's existing tag selector — the same modal the card's
 * tag flow uses — so this adds an entry point, not a second editing surface.
 * Viewers who may not edit get neither `Add` nor `×`.
 *
 * **The affordance rides on EVERY editable row, not only empty ones.** It used
 * to live inside the `no tags` branch alone, so the moment a player carried one
 * tag the Table lost its only way to attach a second — the tagged branch drew
 * chips and `+N` and nothing else. Found while chasing a different report
 * (2026-08-07) that turned out to be the ownership gate below working
 * correctly; this gap is unrelated to it and real on a board the viewer DOES
 * own. The tagged row gets the `+` glyph without the `Add` word: the cell is
 * `min-w-40` and already spends its width on up to two chips and a `+N`, and
 * next to filled chips the bare glyph is unambiguous.
 * @param row - Row being rendered
 * @param ctx - Shared cell context
 * @returns The cell content
 */
const renderTagsCell = (row: PipelineTableRow, ctx: CellContext) => {
  const { createAddTagHandler } = ctx;

  if (row.tags.length === 0) {
    return createAddTagHandler
      ? renderAddTag(row, createAddTagHandler, true)
      : renderEmpty();
  }

  const visible = row.tags.slice(0, MAX_VISIBLE_CHIPS);

  return (
    <Stack direction="horizontal" className="items-center gap-2">
      {visible.map(tag => (
        <TagChip
          key={tag.id || tag.name}
          row={row}
          tag={tag}
          createTagRemoveHandler={ctx.createTagRemoveHandler}
        />
      ))}
      {renderOverflow(row.tags.length - visible.length)}
      {createAddTagHandler
        ? renderAddTag(row, createAddTagHandler, false)
        : null}
    </Stack>
  );
};

/**
 * Watchlist memberships, collapsing the rest into `+N`.
 * @remarks
 * An empty cell — NOT `- None` — when the player is on no watchlist. The
 * design draws the literal string `- None` and an earlier cut reproduced it;
 * Raniel overruled that ("em vez de aparecer '- None' no list na tabela, exiba
 * vazio, None é estranho"). His ruling postdates the artboard and postdates the
 * prototype that still shows it, so blank wins. Not the em-dash placeholder
 * either: that reads "we do not know", and here we do know — the answer is
 * "no lists".
 *
 * Live since 2026-08-07: `ListKanbanCards` selects `watchlists { id name }`, so
 * a blank cell now means the player really is on no list, rather than the board
 * never having asked. A row with no chips is therefore data, not a gap.
 * @param row - Row being rendered
 * @returns The cell content
 */
const renderWatchlistsCell = (row: PipelineTableRow) => {
  if (row.watchlists.length === 0) return null;

  const visible = row.watchlists.slice(0, MAX_VISIBLE_CHIPS);

  return (
    <Stack direction="horizontal" className="items-center gap-1">
      {visible.map(list => (
        <Box key={list.id} className={`self-start ${LIST_CHIP}`}>
          <Text
            variant="micro"
            numberOfLines={1}
            className="max-w-40 text-content-secondary"
          >
            {list.name}
          </Text>
        </Box>
      ))}
      {renderOverflow(row.watchlists.length - visible.length)}
    </Stack>
  );
};

/**
 * The card's current stage (kanban phase) name — SE-6870.
 * @remarks
 * Every row of a pipeline board is by definition in THIS pipeline, so the
 * previous constant `In pipeline` told the reader nothing; what a scout
 * scanning the table needs is WHERE in the funnel the card sits. The cell now
 * renders `row.stageName` — the same `column.title` the Kanban column headers
 * draw (`generateColumnsData` sets it from `phase.name`), so the two views
 * cannot disagree (SE-5491 parity) and a stage move is reflected the moment
 * the board data refreshes. Rows in the defensive unstaged bucket show that
 * bucket's title, exactly as the Kanban lane does.
 *
 * Neutral secondary ink, not the old status-blue: a stage name is data like
 * Activity or List, not a status signal.
 * @param row - Row being rendered
 * @returns The cell content
 */
const renderPipelineCell = (row: PipelineTableRow) => (
  <Text variant="caption" className="text-content-secondary">
    {row.stageName}
  </Text>
);

/**
 * Latest report's score.
 * @remarks
 * The design's Verdict column is a categorical judgement — a green
 * `Starting XI`/`Investment`, an amber `Pass`, a red `Gap`. **The schema has no
 * such field.** A scout report carries `overallScore` (a float) and `qnaText`
 * (free prose); there is no verdict, would-sign, or recommendation enum
 * anywhere in it. `verdict` here is therefore the most recent report's SCORE,
 * exactly as the Kanban card and the preview drawer render it, so grading it
 * green or red would be this renderer inventing a judgement nobody wrote.
 *
 * ## Why this is a bare badge and no longer `StatusChip` (2026-08-07)
 *
 * `StatusChip`'s root is a `Stack` carrying NO self-alignment. A table cell is
 * a flex column, whose cross-axis default is `stretch`, so the chip grew to the
 * full width of the Verdict column and rendered as the wide dark-green slab
 * Raniel reported — the same structural failure the GPR badge needed
 * `self-center` to escape, not a padding problem. Its leading check icon plus
 * `gap-1` then added ~14px on top of that.
 *
 * The fix takes GPR's chrome wholesale (`self-start` for a left-aligned column,
 * `min-w-10`, `rounded-none`, 8/4 inset, micro text) and keeps the verdict's own
 * two colours. **The check icon is dropped**: GPR carries none, and "only the
 * colours differ" cannot hold while this one spends a glyph and a gap that GPR
 * does not. Nothing is lost — the icon marked "scouted and scored" (SE-6490),
 * which the presence of a score already says.
 *
 * The chrome is COPIED from `renderGprBadge` rather than shared because sharing
 * would mean parameterising the scouting table's badge on colour, i.e. turning
 * another feature's GPR badge into a generic one. If GPR's chrome moves, move
 * `VERDICT_CHIP` with it.
 *
 * `row.verdict` is already the score string rounded to ONE decimal by
 * `formatVerdictScore` (e.g. "3.8"). GPR's `.toFixed(0)` is deliberately NOT
 * inherited: GPR is a whole-number rating, where this is a 0–10 score whose
 * decimal is the signal.
 * @param row - Row being rendered
 * @returns The cell content
 */
const renderVerdictCell = (row: PipelineTableRow) =>
  row.verdict == null ? (
    renderEmpty()
  ) : (
    <Box className={`${VERDICT_CHIP} ${VERDICT_TONE.box}`}>
      <Text variant="micro" className={`text-center ${VERDICT_TONE.text}`}>
        {row.verdict}
      </Text>
    </Box>
  );

/** Column key → cell renderer. Exhaustive by construction. */
const CELL_RENDERERS: Record<
  PipelineTableColumnKey,
  (row: PipelineTableRow, ctx: CellContext) => ReactNode
> = {
  name: renderNameCell,
  /* `numberOfLines={1}` so the chip can never grow a second line again, even
     if a longer position code or a narrower viewport turns up. The column is
     now wide enough that this should never truncate. */
  position: row =>
    row.position ? (
      <Box className={`self-start ${POS_CHIP}`}>
        <Text
          variant="caption"
          numberOfLines={1}
          className="text-content-secondary"
        >
          {row.position}
        </Text>
      </Box>
    ) : (
      renderEmpty()
    ),
  team: renderClubCell,
  /* The SCOUTING table's badge, imported rather than reimplemented — Raniel's
     ruling, 2026-08-07: "usa a referência desse componente do GPR como está
     implementado na tabela de scouting". Sharing the function means the two
     tables cannot drift, and it settles the colour question by deferring it:
     whatever scouting decides GPR looks like, the board inherits, with zero
     judgement added here. (As it stands scouting draws every GPR on one flat
     blue pairing — there is no value-to-colour banding to inherit.) */
  gpr: row =>
    row.gpr == null ? renderEmpty() : renderGprBadge(row.gpr, "center"),
  /* The design sets Age two steps below its neighbours (14 against 16). */
  age: row =>
    row.age == null ? (
      renderEmpty()
    ) : (
      <Text variant="body" className="text-content-secondary">
        {String(row.age)}
      </Text>
    ),
  tags: renderTagsCell,
  contractMonths: renderContractMonthsCell,
  activity: row => {
    const label = formatRelativeAgo(row.updatedAt);
    return label === null ? (
      renderEmpty()
    ) : (
      <Text variant="caption" className="text-content-muted">
        {label}
      </Text>
    );
  },
  watchlists: renderWatchlistsCell,
  pipeline: renderPipelineCell,
  /* 0 is an answer ("nobody has scouted this player"), not a missing value, so
     the count renders rather than falling back to the placeholder. */
  reports: row =>
    row.reportsCount == null
      ? renderEmpty()
      : renderValue(String(row.reportsCount)),
  verdict: renderVerdictCell,
  actions: () => null,
};

/**
 * The sort glyph for one header.
 * @remarks
 * The design draws one on every LABELLED header, so every real column sorts —
 * the alternative was a chevron on columns that ignore it. The inactive glyph
 * is the two-way chevron (the affordance: "this sorts"); the active column
 * swaps it for the arrow that names the direction in force. The trailing slot
 * is not a column and gets nothing.
 * @param column - Column definition
 * @param isActive - Whether this column owns the active sort
 * @param direction - Active sort direction
 * @returns The glyph element, or null for a column that does not sort
 */
const renderSortGlyph = (
  column: PipelineTableColumn,
  isActive: boolean,
  direction: PipelineTableSortDirection
) => {
  /* The trailing slot has no sortKey and nothing to sort, but the glyph used
     to render for EVERY column — so a lone two-way chevron floated after
     Verdict, offering a sort that does not exist (Raniel, 2026-08-07). The
     glyph is an affordance, so it renders only where the affordance is real;
     this is the same gate the `TableHead` below already applies to the press
     target, now applied to the thing that advertises it. */
  if (column.sortKey == null) return null;

  if (!isActive) {
    return (
      <Icon as={ChevronsUpDown} size="sm" className="text-content-subtle" />
    );
  }

  return (
    <Icon
      as={direction === "asc" ? ChevronUp : ChevronDown}
      size="sm"
      className="text-content-secondary"
    />
  );
};

/**
 * Render one header cell — a press target when the column sorts.
 * @param column - Column definition
 * @param sortKey - Active sort column
 * @param sortDirection - Active sort direction
 * @param createSortHandler - Builds the header's press handler
 * @returns The header cell element
 */
const renderHeadCell = (
  column: PipelineTableColumn,
  sortKey: PipelineTableSortKey | null,
  sortDirection: PipelineTableSortDirection,
  createSortHandler: (key: PipelineTableSortKey) => () => void
) => {
  const isActive = column.sortKey != null && column.sortKey === sortKey;
  const label = (
    <Stack
      direction="horizontal"
      className={`items-center gap-2 ${column.isCentered ? "justify-center" : ""}`}
    >
      <Text variant="title-sm" className="text-content-secondary">
        {column.label}
      </Text>
      {renderSortGlyph(column, isActive, sortDirection)}
    </Stack>
  );

  return (
    <TableHead
      key={column.key}
      data-testid={TEST_ID_PLAYER_KANBAN.tableHeaderFor(column.key)}
      className={`h-12 border-b border-outline-subtle py-3 ${column.cellClassName}`}
    >
      {column.sortKey == null ? (
        label
      ) : (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Sort by ${column.label}`}
          onPress={createSortHandler(column.sortKey)}
        >
          {label}
        </Pressable>
      )}
    </TableHead>
  );
};

/**
 * Render one data row.
 * @param row - Row to render
 * @param isLast - Whether this is the final row (the design drops its rule)
 * @param ctx - Shared cell context
 * @returns The row element
 */
const renderRow = (
  row: PipelineTableRow,
  isLast: boolean,
  ctx: CellContext
) => (
  <TableRow
    key={`${row.stageId}-${row.playerId}`}
    // SE-5491: `<stageId>.<playerId>` identity so the dual-view consistency
    // check can compare per-stage card-ID sets across both renderers. The e2e
    // dual-view spec reads exactly this — do not change the shape.
    data-testid={TEST_ID_PLAYER_KANBAN.tableRowFor(row.stageId, row.playerId)}
    // The hovered row's fill is a real CSS `:hover`, not a JS hover state:
    // the data-driven form strands its attribute when the pointer leaves fast
    // (the same fix the drawer's Add button and the board's CTA needed).
    //
    // The rule under the row is the ROW's own border, not one per cell, so the
    // two do not stack. It is `outline-default` (221,220,219) where the header
    // takes `outline-subtle` (230,230,230): the design draws those two rules at
    // different weights, and the token tier happens to carry both exactly.
    className={`web:hover:bg-surface-raised h-12 bg-surface-base ${
      isLast ? "border-b-0" : "border-b border-outline-default"
    }`}
  >
    {COLUMNS.map(column => (
      <TableData
        key={column.key}
        className={`py-3 ${column.isCentered ? "items-center" : ""} ${column.cellClassName}`}
      >
        {CELL_RENDERERS[column.key](row, ctx)}
      </TableData>
    ))}
  </TableRow>
);

/**
 * The bordered table card — the element both scroll hosts wrap.
 * @remarks
 * The outer border is ROUNDED, not square (radius 12, the rung the design
 * draws). Clipping the card's own content is what makes it read as one:
 * without it the header fill and the rows' own base / hover fills paint
 * square right over the curve, and the radius only shows on the last row's
 * two bottom corners.
 *
 * SE-6873: the card is sized to its CONTENT, floored at the container's
 * width (fit-content via cross-axis self-alignment, plus a full-width
 * minimum). Stretched (the default), the card was pinned to the container
 * and its clipping CUT OFF any column past the right edge; content sizing
 * lets the card grow with the table so the scroll container can bring every
 * column into view. When the columns fit, the full-width minimum keeps the
 * card exactly as wide as before — and the scroll container shows no bar.
 * @param props - The view's props, passed through whole
 * @param props.rows - Rows to render, already flattened and sorted
 * @param props.sortKey - Active sort column
 * @param props.sortDirection - Active sort direction
 * @param props.createSortHandler - Builds the press handler for a sortable header
 * @param props.createRowPressHandler - Builds the press handler for a row
 * @param props.createTagRemoveHandler - Builds a tag's remove handler
 * @param props.createAddTagHandler - Builds a row's add-tag handler
 * @returns The bordered card wrapping the table
 */
const renderTableCard = ({
  rows,
  sortKey,
  sortDirection,
  createSortHandler,
  createRowPressHandler,
  createTagRemoveHandler,
  createAddTagHandler,
}: PipelineTableViewProps) => (
  <Box className="min-w-full self-start overflow-hidden rounded-lg border border-outline-subtle bg-surface-base">
    <Table className="w-full">
      <TableHeader>
        <TableRow className="bg-surface-base">
          {COLUMNS.map(column =>
            renderHeadCell(column, sortKey, sortDirection, createSortHandler)
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, index) =>
          renderRow(row, index === rows.length - 1, {
            createRowPressHandler,
            createTagRemoveHandler,
            createAddTagHandler,
          })
        )}
      </TableBody>
    </Table>
  </Box>
);

/**
 * Table renderer for the player pipeline (SE-5491; laid out per the SE-6554
 * pipeline Table design).
 * @remarks
 * The Kanban board's alternate presentation of the SAME dataset — the rows are
 * built from `useKanbanBoard`'s `columnsData`, so stage membership and
 * per-stage order are identical in both views by construction.
 *
 * The HORIZONTAL page gutter matches the Kanban branch's 32px gutter
 * (MainView) so that toggling between the two renderers does not shift the
 * content's left edge under a toolbar that has not moved.
 *
 * The TOP inset deliberately does not match: the table sits 24px below the
 * toolbar's bottom rule (Raniel, 2026-08-06), where the Kanban branch still
 * keeps its own 8px rhythm. This layer owns it because it is the same layer
 * the Kanban branch owns its own gutter on — a wrapper outside the scrolling
 * content — so the two rhythms stay comparable and a later ruling can align
 * them by editing one class on each side.
 *
 * The bordered table card — the element both scroll hosts wrap — is built by
 * {@link renderTableCard}; the branch below only chooses its scroll container
 * (SE-6873).
 * @param props - Component props; see {@link PipelineTableViewProps}
 */
const PipelineTableView = (props: PipelineTableViewProps) => (
  <Box
    testID={TEST_ID_PLAYER_KANBAN.TABLE_VIEW}
    className="flex-1 overflow-hidden bg-surface-base px-8 pb-2 pt-4"
  >
    {props.rows.length === 0 ? (
      <Box
        testID={TEST_ID_PLAYER_KANBAN.TABLE_EMPTY}
        className="items-center justify-center py-12"
      >
        <Text variant="body" className="text-content-muted">
          No players on this board yet.
        </Text>
      </Box>
    ) : IS_WEB ? (
      /* SE-6873: ONE CSS overflow container owns BOTH axes, the same
         pattern AllTargetsTableView proved out (its module note): both
         scrollbars pin to the visible edges, trackpad and shift+wheel
         gestures work natively, and the header can never desync from the
         body because the whole table moves as one element. Two nested
         ScrollViews cannot do this — each axis's bar would sit at the far
         edge of the CONTENT, off-screen until the other axis is scrolled.
         This is a capability fork (CSS overflow vs RN ScrollView), not a
         layout fork — both platforms render the identical table. */
      <Box className="min-h-0 flex-1 overflow-auto">
        {renderTableCard(props)}
      </Box>
    ) : (
      /* Native keeps its vertical ScrollView unchanged — the reported
         defect is desktop-web (scrollbar / shift+wheel are desktop
         inputs), and scouting's table forks on exactly this line. */
      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        {renderTableCard(props)}
      </ScrollView>
    )}
  </Box>
);

PipelineTableView.displayName = "PipelineTableView";

export default memo(PipelineTableView);
